<template>
  <div class="login-container">
    <div class="login-box">
      <div class="tabs">
        <button 
          :class="['tab-btn', { active: activeTab === 'login' }]"
          @click="activeTab = 'login'"
        >
          {{ $t('login.title') }}
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'register' }]"
          @click="activeTab = 'register'"
        >
          {{ $t('login.register') }}
        </button>
      </div>

      <form v-if="activeTab === 'login'" @submit.prevent="handleLogin" class="form">
        <input
          v-model="loginForm.email"
          type="email"
          :placeholder="$t('login.email')"
          required
        />
        <password-input
          v-model="loginForm.password"
          :placeholder="$t('login.password')"
          autocomplete="current-password"
        />
        <button 
          type="submit" 
          class="submit-btn" 
          :disabled="!isLoginFormValid"
        >
          {{ $t('login.loginButton') }}
        </button>

        <div class="separator">
          <span>{{ $t('login.orContinueWith') }}</span>
        </div>

        <button 
          type="button" 
          class="social-btn google" 
          @click="socialLogin('google')"
        >
          <i class="fab fa-google"></i> {{ $t('login.continueWithGoogle') }}
        </button>
      </form>

      <form v-else @submit.prevent="handleRegister" class="form">
        <input
          v-model="registerForm.username"
          type="text"
          :placeholder="$t('login.username')"
          required
        />
        <input
          v-model="registerForm.email"
          type="email"
          :placeholder="$t('login.email')"
          required
        />
        <password-input
          v-model="registerForm.password"
          :placeholder="$t('login.password')"
          autocomplete="new-password"
        />
        <password-input
          v-model="registerForm.confirmPassword"
          :placeholder="$t('login.confirmPassword')"
          autocomplete="new-password"
        />
        
        <div class="terms-privacy">
          <input type="checkbox" id="agreement" v-model="agreeToTerms" required>
          <label for="agreement">{{ $t('login.agreeToTerms') }} 
            <a href="#" @click.prevent="showTerms">{{ $t('login.terms') }}</a> {{ $t('login.and') }} 
            <a href="#" @click.prevent="showPrivacyPolicy">{{ $t('login.privacyPolicy') }}</a>
          </label>
        </div>
        
        <button 
          type="submit" 
          class="submit-btn" 
          :disabled="!isRegisterFormValid"
        >
          {{ $t('login.registerButton') }}
        </button>

        <div class="separator">
          <span>{{ $t('login.orContinueWith') }}</span>
        </div>

        <button 
          type="button" 
          class="social-btn google" 
          @click="socialLogin('google')"
        >
          <i class="fab fa-google"></i> {{ $t('login.continueWithGoogle') }}
        </button>
      </form>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div class="fast-register-promo" v-if="activeTab === 'login'">
        <div class="promo-content">
          <h3>{{ $t('login.newToDeepRing') }}</h3>
          <p><i class="fas fa-coins"></i> {{ $t('login.getFreeCoins') }}</p>
          <router-link to="/register" class="fast-register-btn">
            <i class="fas fa-bolt"></i> {{ $t('login.quickRegister') }}
          </router-link>
        </div>
      </div>

      <div class="social-links">
        <a href="https://www.linkedin.com/company/101185636/admin/dashboard/" target="_blank"><i class="fab fa-linkedin"></i></a>
        <a href="https://www.instagram.com/unligames/" target="_blank"><i class="fab fa-instagram"></i></a>
        <a href="https://twitter.com/Unligamestudio" target="_blank"><i class="fab fa-twitter"></i></a>
        <a href="https://t.me/+byBMdvSfpEVmMTdh" target="_blank"><i class="fab fa-telegram"></i></a>
        <a href="https://www.youtube.com/@UnliGamesStudio" target="_blank"><i class="fab fa-youtube"></i></a>
      </div>
    </div>
    
    <privacy-policy 
      :show="privacyPolicyVisible" 
      @close="privacyPolicyVisible = false"
      @accept="acceptPrivacyPolicy"
    />
  </div>
</template>

<script>
import PasswordInput from '@/shared/components/passwordInput/PasswordInput.vue'
import PrivacyPolicy from './components/PrivacyPolicy.vue'
import { login, register } from '@/core/services/auth'
import Cookies from 'js-cookie'

export default {
  name: 'LoginPage',
  components: {
    PasswordInput,
    PrivacyPolicy
  },
  data() {
    return {
      activeTab: 'login',
      loginForm: {
        email: '',
        password: ''
      },
      registerForm: {
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
      },
      agreeToTerms: false,
      privacyPolicyVisible: false,
      error: null
    }
  },
  computed: {
    isLoginFormValid() {
      return this.loginForm.email && 
             this.loginForm.password && 
             this.validateEmail(this.loginForm.email);
    },
    isRegisterFormValid() {
      return this.registerForm.username && 
             this.registerForm.username.length >= 3 &&
             this.registerForm.email && 
             this.validateEmail(this.registerForm.email) &&
             this.registerForm.password && 
             this.registerForm.password.length >= 6 &&
             this.registerForm.confirmPassword && 
             this.registerForm.password === this.registerForm.confirmPassword &&
             this.agreeToTerms;
    }
  },
  methods: {
    validateEmail(email) {
      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    },
    
    showPrivacyPolicy() {
      this.privacyPolicyVisible = true;
    },
    
    showTerms() {
      this.privacyPolicyVisible = true;
    },
    
    acceptPrivacyPolicy() {
      this.agreeToTerms = true;
    },
    
    async handleLogin() {
      this.error = null
      try {
        const response = await login(this.loginForm)
        Cookies.set('jwt', response.jwt)
        await this.$store.dispatch('auth/tryAutoLogin')
        this.$router.push('/home')
      } catch (error) {
        this.error = error.response?.data?.error?.message || 'Falha ao entrar'
        console.error(error)
      }
    },
    async handleRegister() {
      this.error = null
      
      if (!this.agreeToTerms) {
        this.error = 'Você deve concordar com os termos e política de privacidade';
        return;
      }
      
      if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.error = 'As senhas não coincidem'
        return
      }
      try {
        const userData = {
          username: this.registerForm.username,
          email: this.registerForm.email,
          password: this.registerForm.password
        }
        
        const response = await register(userData)
        Cookies.set('jwt', response.jwt)
        
        await this.$store.dispatch('auth/tryAutoLogin')
        
        try {
          await this.$store.dispatch('player/createPlayerProfile', {
            userId: response.user.id,
            coins: 5000,
            level: 1,
            experience: 0
          })
          
          await this.$store.dispatch('player/loadPlayerData')
          
          this.$router.push('/home')
        } catch (profileError) {
          console.warn('Não foi possível criar o perfil do jogador:', profileError)
          this.$router.push('/home')
        }
      } catch (error) {
        const errorMessage = error.response?.data?.error?.message || 'Falha no registro'
        this.error = errorMessage
        if (errorMessage.includes('already taken')) {
          this.error = 'Este nome de usuário ou email já está registrado'
        }
      }
    },
    socialLogin(provider) {
      if (provider === 'google') {
        const strapiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';
        const frontendUrl = window.location.origin;
        const callbackUrl = `${frontendUrl}/auth-callback`;
        
        const authUrl = `${strapiUrl}/api/connect/${provider}?callback=${encodeURIComponent(callbackUrl)}`;
        
        console.log('Iniciando autenticação Google, redirecionando para:', authUrl);
        window.location.href = authUrl;
      } else {
        this.error = `Login social com ${provider} não está disponível.`;
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.9)),
              url('@/assets/images/arenas/cave_arena.png') center/cover;
  padding: 1rem;
}

.login-box {
  background: rgba(20, 20, 20, 0.95);
  border: 1px solid rgba(255, 0, 0, 0.2);
  border-radius: 8px;
  padding: 1.5rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 0 20px rgba(255, 0, 0, 0.1);
}

.tabs {
  display: flex;
  margin-bottom: 1.5rem;
}

.tab-btn {
  flex: 1;
  padding: 0.8rem;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.3s;
  font-size: 0.95rem;
}

.tab-btn.active {
  border-bottom-color: #ff3e3e;
  font-weight: bold;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.form input {
  padding: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: #fff;
  font-size: 1rem;
}

.submit-btn {
  margin-top: 1rem;
  padding: 0.8rem;
  background: #ff3e3e;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.3s;
  font-size: 1rem;
}

.submit-btn:hover {
  background: #ff2525;
}

.social-links {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
  gap: 1.2rem;
}

.social-links a {
  color: #fff;
  font-size: 1.25rem;
  transition: color 0.3s;
}

.social-links a:hover {
  color: #ff3e3e;
}

.error-message {
  color: #ff3e3e;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.85rem;
}

.fast-register-promo {
  margin-top: 1.5rem;
  background: rgba(255, 62, 62, 0.1);
  border-radius: 8px;
  padding: 1.2rem;
  text-align: center;
}

.promo-content h3 {
  color: #fff;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.promo-content p {
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0.8rem;
  font-size: 0.9rem;
}

.fast-register-btn {
  display: inline-block;
  background: #ff3e3e;
  color: #fff;
  text-decoration: none;
  padding: 0.7rem 1rem;
  border-radius: 4px;
  font-weight: bold;
  transition: background 0.3s;
  font-size: 0.9rem;
}

.fast-register-btn:hover {
  background: #ff2525;
}

.submit-btn:disabled {
  background: #999;
  cursor: not-allowed;
}

.submit-btn:disabled:hover {
  background: #888;
}

.terms-privacy {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 0.5rem;
}

.terms-privacy input[type="checkbox"] {
  margin-top: 3px;
}

.terms-privacy a {
  color: #ff3e3e;
  text-decoration: none;
}

.separator {
  display: flex;
  align-items: center;
  margin: 1.2rem 0;
}

.separator::before,
.separator::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.separator span {
  padding: 0 10px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.9rem;
}

.social-btn {
  padding: 0.7rem;
  border-radius: 4px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: bold;
  transition: opacity 0.3s;
  width: 100%;
  font-size: 0.9rem;
}

.social-btn:hover {
  opacity: 0.9;
}

.social-btn.google {
  background: #fff;
  color: #444;
}

/* Media queries for better responsiveness */
@media (max-width: 480px) {
  .login-box {
    padding: 1.2rem;
    max-width: 100%;
  }
  
  .tabs {
    margin-bottom: 1.2rem;
  }
  
  .tab-btn {
    padding: 0.7rem 0.5rem;
    font-size: 0.9rem;
  }
  
  .form input,
  .submit-btn,
  .social-btn {
    font-size: 0.9rem;
    padding: 0.7rem;
  }
  
  .social-links {
    gap: 1rem;
  }
  
  .social-links a {
    font-size: 1.2rem;
  }
  
  .fast-register-promo {
    padding: 1rem;
  }
  
  .promo-content h3 {
    font-size: 1rem;
  }
  
  .promo-content p,
  .fast-register-btn {
    font-size: 0.85rem;
  }
  
  .terms-privacy {
    font-size: 0.75rem;
  }
}

@media (max-height: 700px) {
  .login-box {
    padding: 1rem;
  }
  
  .form {
    gap: 0.7rem;
  }
  
  .social-links {
    margin-top: 1rem;
  }
  
  .fast-register-promo {
    margin-top: 1rem;
    padding: 0.8rem;
  }
}
</style>
